import { Check, X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { Button } from "../../Button";
import { cn } from "../../utils";

interface FileTreeInlineEditorProps {
  value: string;
  error: string | null;
  placeholder: string;
  paddingLeft: number;
  showBranch?: boolean;
  treeGuides?: ReactNode;
  leadingContent: ReactNode;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const FileTreeInlineEditor = ({
  value,
  error,
  placeholder,
  paddingLeft,
  showBranch = false,
  treeGuides,
  leadingContent,
  onChange,
  onSubmit,
  onCancel,
}: FileTreeInlineEditorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <div
      className={cn(
        "relative space-y-1",
        showBranch &&
          "before:pointer-events-none before:absolute before:left-0 before:top-4 before:z-10 before:w-3 before:border-t before:border-gray-200 before:content-['']",
      )}
      style={{ paddingLeft: `${paddingLeft}px` }}
    >
      {treeGuides}
      <div className="relative z-10 min-w-0 space-y-1">
        <div
          className={cn(
            "flex h-8 items-center gap-1.5 rounded-md border bg-white px-2 shadow-sm",
            error ? "border-red-300" : "border-blue-200",
          )}
          onClick={event => event.stopPropagation()}
          onBlur={event => {
            const nextFocusedElement = event.relatedTarget;
            if (
              nextFocusedElement instanceof Node &&
              event.currentTarget.contains(nextFocusedElement)
            ) {
              return;
            }

            onCancel();
          }}
        >
          <div className="flex shrink-0 items-center gap-1.5 text-gray-500">{leadingContent}</div>
          <input
            ref={inputRef}
            value={value}
            onChange={event => onChange(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                onSubmit();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                onCancel();
              }
            }}
            placeholder={placeholder}
            className="h-7 min-w-0 flex-1 border-0 bg-transparent px-0 py-0 text-[13px] shadow-none outline-none focus-visible:ring-0"
          />
          <div className="flex shrink-0 items-center gap-0.5">
            <Button type="button" variant="ghost" size="tinyIcon" onClick={onSubmit} title="Confirm">
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="tinyIcon" onClick={onCancel} title="Cancel">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {error && <p className="pl-6 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
};
